import type { Issue } from '../types/issue';
import { createdOf, resolvedOf, CREATED_KEYS, RESOLVED_KEYS } from './jira-dates';
import { monthKeyOf } from './months';
import { isDone } from './status';
import { cpsTicketId } from './customer-bugs';

export type MonthlyTrend = {
  openedStories: number;
  openedBugs: number;
  openedCustomerBugs: number;   // Bugs mit CPS-Verknüpfung (Teilmenge von openedBugs)
  openedInternalBugs: number;   // Bugs ohne CPS-Verknüpfung
  closedStories: number;
  closedBugs: number;
  closedCustomerBugs: number;   // Teilmenge von closedBugs
  closedInternalBugs: number;
};

export type TrendIndex = Map<string, MonthlyTrend>;

// Prüft, ob in den Daten überhaupt eines der Created/Resolved-Felder
// gepflegt ist. Wir akzeptieren mehrere Schreibweisen (JIRA exportiert
// je nach Locale unterschiedlich).
export function trendFieldsAvailable(rows: Issue[]): { created: boolean; resolved: boolean } {
  let created = false, resolved = false;
  for (const r of rows) {
    if (!created && CREATED_KEYS.some(k => {
      const v = (r as Record<string, unknown>)[k];
      return typeof v === 'string' && v;
    })) created = true;
    if (!resolved && RESOLVED_KEYS.some(k => {
      const v = (r as Record<string, unknown>)[k];
      return typeof v === 'string' && v;
    })) resolved = true;
    if (created && resolved) break;
  }
  return { created, resolved };
}

// Counts der Created/Closed Stories und Bugs pro Monat.
//   Opened (Created):  jedes Story/Bug mit Created-Datum, egal welcher Status.
//   Closed:            Status ∈ { closed, resolved, aborted } UND Resolved-
//                      Datum vorhanden → Bucket-Monat = Resolved-Datum.
// Der Status-Filter macht die Zählung robust gegen Reopen-Szenarien und
// Aborted-Workflows, die die Resolution nicht setzen.
export function buildTrendIndex(rows: Issue[]): TrendIndex {
  const idx: TrendIndex = new Map();
  const ensure = (mk: string): MonthlyTrend => {
    let v = idx.get(mk);
    if (!v) { v = emptyMonth(); idx.set(mk, v); }
    return v;
  };

  for (const r of rows) {
    const t = r['Issue Type'];
    if (t !== 'Story' && t !== 'Bug') continue;
    const isBug = t === 'Bug';
    const isCustomer = isBug && cpsTicketId(r) != null;

    const c = createdOf(r);
    if (c) {
      const m = ensure(monthKeyOf(c));
      if (isBug) {
        m.openedBugs++;
        if (isCustomer) m.openedCustomerBugs++; else m.openedInternalBugs++;
      } else {
        m.openedStories++;
      }
    }

    if (isDone(r['Status'] ?? '')) {
      const rs = resolvedOf(r);
      if (rs) {
        const m = ensure(monthKeyOf(rs));
        if (isBug) {
          m.closedBugs++;
          if (isCustomer) m.closedCustomerBugs++; else m.closedInternalBugs++;
        } else {
          m.closedStories++;
        }
      }
    }
  }
  return idx;
}

export function emptyMonth(): MonthlyTrend {
  return {
    openedStories: 0, openedBugs: 0,
    openedCustomerBugs: 0, openedInternalBugs: 0,
    closedStories: 0, closedBugs: 0,
    closedCustomerBugs: 0, closedInternalBugs: 0,
  };
}

// Rolling-30-Tage-Sicht: Created/Closed in den letzten 30 Tagen ab jetzt.
// Ergänzung zur Monatsscheiben-Sicht (buildTrendIndex).
export type Rolling30 = {
  windowStart:  Date;                  // heute - 30 Tage (00:00)
  windowEnd:    Date;                  // jetzt
  createdTotal: number;
  closedTotal:  number;
  stories:      { created: number; closed: number };
  bugs:         { created: number; closed: number };
  bugsCustomer: { created: number; closed: number };
  bugsInternal: { created: number; closed: number };
};

export function computeRolling30(rows: Issue[]): Rolling30 {
  const now = new Date();
  const cutoff = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  const inWindow = (d: Date | null): boolean =>
    d != null && d >= cutoff && d <= now;

  const out: Rolling30 = {
    windowStart:  cutoff,
    windowEnd:    now,
    createdTotal: 0,
    closedTotal:  0,
    stories:      { created: 0, closed: 0 },
    bugs:         { created: 0, closed: 0 },
    bugsCustomer: { created: 0, closed: 0 },
    bugsInternal: { created: 0, closed: 0 },
  };

  for (const r of rows) {
    const t = r['Issue Type'];
    if (t !== 'Story' && t !== 'Bug') continue;
    const isBug = t === 'Bug';
    const isCustomer = isBug && cpsTicketId(r) != null;

    if (inWindow(createdOf(r))) {
      out.createdTotal++;
      if (isBug) {
        out.bugs.created++;
        if (isCustomer) out.bugsCustomer.created++;
        else            out.bugsInternal.created++;
      } else {
        out.stories.created++;
      }
    }

    if (isDone(r['Status'] ?? '') && inWindow(resolvedOf(r))) {
      out.closedTotal++;
      if (isBug) {
        out.bugs.closed++;
        if (isCustomer) out.bugsCustomer.closed++;
        else            out.bugsInternal.closed++;
      } else {
        out.stories.closed++;
      }
    }
  }
  return out;
}
